import { useState, useCallback, useEffect, useRef } from 'react'
import { TemplateItem } from '../types/template'
import { useTableOperations } from '@/custom-hooks/useTableOperations'
import { useGetTemplateIdList, useGetTemplateList } from '../../utils'

// 表格操作配置
const tableOperationsConfig = {
  filterFieldMap: {
    originType: 'originType',
  },
  sortFieldMap: {
    resourceAmount: 'TemplateSortFieldResourceAmount',
    updateTime: 'TemplateSortFieldUpdateTime',
  },
  enableSorting: true,
  enableFiltering: true,
  enablePagination: true,
}

export const useTemplateData = (
  taskParams: any = {},
  onTemplateSelectionChange?: (
    selectedTemplates: string[],
    executeUpdateTemplates: string[],
  ) => void,
) => {
  // 搜索参数状态
  const [searchParams, setSearchParams] = useState({
    page: 1,
    pageSize: 10,
    os: taskParams.os || undefined,
    filterParams: {
      sceneIds: [11],
      verificationModeIds: taskParams.verificationModeIds || [],
    },
  })

  // 选中的模板和执行更新模板
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>(
    taskParams.selectedTemplates || [],
  )
  const [executeUpdateTemplates, setExecuteUpdateTemplates] = useState<string[]>(
    taskParams.executeUpdateTemplates || [],
  )

  // 记录 isLatest 状态
  const isLatest = taskParams.isLatest || false

  // 模板数据状态
  const [templates, setTemplates] = useState<TemplateItem[]>([])

  // 防抖定时器
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  // 获取模板列表
  const [getTemplateList, getTemplateListLoading, total] = useGetTemplateList()
  // 获取模板id列表
  const [getTemplateIdList, templateIds] = useGetTemplateIdList()

  // 使用表格操作hook
  const { handleTableChange, handleTableSelectChange } = useTableOperations({
    searchParams,
    setSearchParams,
    selectedRowKeys: selectedTemplates,
    setSelectedRowKeys: (keys: React.Key[]) => setSelectedTemplates(keys as string[]),
    config: tableOperationsConfig,
  })

  // 当搜索参数变化时重新获取数据
  const fetchData = async () => {
    const res = await getTemplateList(searchParams)
    const resp = await getTemplateIdList({
      ...searchParams,
      page: undefined,
      pageSize: undefined,
      os: undefined,
    })
    if (!res || !res.data || !resp || !resp.data) return
    setTemplates(res.data.list)
  }

  // 监听 taskParams 变化，更新搜索参数
  useEffect(() => {
    setSearchParams(prev => ({
      ...prev,
      os: taskParams.os || '',
      filterParams: {
        ...prev.filterParams,
        verificationModeIds: taskParams.verificationModeIds || [],
      },
    }))
  }, [taskParams.os, taskParams.verificationModeIds])

  // 监听 isLatest 变化，更新执行更新列表
  useEffect(() => {
    if (isLatest && templateIds.length > 0) {
      // isLatest=true 时，将所有模板加入执行更新列表
      setExecuteUpdateTemplates(templateIds)
    } else if (!isLatest) {
      // isLatest=false 时，清空执行更新列表
      setExecuteUpdateTemplates([])
    }
  }, [isLatest, templateIds])

  // 监听模板选择状态变化，同步到 taskParams
  useEffect(() => {
    if (onTemplateSelectionChange) {
      onTemplateSelectionChange(selectedTemplates, executeUpdateTemplates)
    }
  }, [selectedTemplates, executeUpdateTemplates, onTemplateSelectionChange])

  useEffect(() => {
    if (taskParams.os && taskParams.verificationModeIds?.length > 0) {
      fetchData()
    }
  }, [searchParams])

  // 智能恢复：检查恢复的模板是否仍然可用
  useEffect(() => {
    if (templateIds.length > 0 && selectedTemplates.length > 0) {
      const validSelectedTemplates = selectedTemplates.filter(id => templateIds.includes(id))

      if (validSelectedTemplates.length !== selectedTemplates.length) {
        // 过滤掉无效的模板选择
        setSelectedTemplates(validSelectedTemplates)
        // 同时过滤掉无效的执行更新模板
        setExecuteUpdateTemplates(prev => prev.filter(id => templateIds.includes(id)))
      }
    }
  }, [templateIds, selectedTemplates])

  // 组件卸载时清理定时器
  useEffect(
    () => () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    },
    [],
  )

  // 搜索模板名称（带防抖）
  const searchTemplates = useCallback((name: string) => {
    // 清除之前的定时器
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    // 设置新的定时器
    debounceTimer.current = setTimeout(() => {
      setSearchParams(prev => ({
        ...prev,
        page: 1,
        filterParams: {
          ...prev.filterParams,
          name,
        },
      }))
    }, 300) // 300ms 防抖延迟
  }, [])

  // 选择模板
  const selectTemplate = useCallback((templateId: string, selected: boolean) => {
    if (selected) {
      setSelectedTemplates(prev => [...prev, templateId])
    } else {
      setSelectedTemplates(prev => prev.filter(id => id !== templateId))
      // 取消选中模板时，同时从执行更新列表移除
      setExecuteUpdateTemplates(prev => prev.filter(id => id !== templateId))
    }
  }, [])

  // 全选/取消全选
  const selectAllTemplates = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedTemplates(templateIds)
      } else {
        setSelectedTemplates([])
        // 取消全选时，清空执行更新列表
        setExecuteUpdateTemplates([])
      }
    },
    [templateIds],
  )

  // 设置执行更新
  const setExecuteUpdate = useCallback((templateId: string, enabled: boolean) => {
    if (enabled) {
      setExecuteUpdateTemplates(prev => [...prev, templateId])
    } else {
      setExecuteUpdateTemplates(prev => prev.filter(id => id !== templateId))
    }
  }, [])

  return {
    templates,
    templateIds,
    loading: getTemplateListLoading,
    pagination: {
      current: searchParams.page,
      pageSize: searchParams.pageSize,
      total: total,
    },
    selectedTemplates,
    executeUpdateTemplates,
    searchTemplates,
    selectTemplate,
    selectAllTemplates,
    setExecuteUpdate,
    handleTableChange,
    handleTableSelectChange,
  }
}
